import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useIsAboveMobile } from '@ordering/hooks/useMediaQuery';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose, DialogTrigger, DialogHeader, DialogFooter } from './dialog';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription, DrawerClose, DrawerTrigger, DrawerHeader, DrawerFooter, DrawerNestedRoot } from './drawer';
interface ResponsiveModalContextValue {
  isDesktop: boolean;
  dismissible: boolean;
}
const ResponsiveModalContext = React.createContext<ResponsiveModalContextValue | null>(null);
const useResponsiveModal = () => {
  const context = React.useContext(ResponsiveModalContext);
  if (!context) {
    throw new Error("ResponsiveModal components must be used within ResponsiveModal");
  }
  return context;
};
type ResponsiveModalProps = {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  shouldScaleBackground?: boolean;
  onAnimationEnd?: (open: boolean) => void;
  repositionInputs?: boolean;
  dismissible?: boolean;
  modal?: boolean;
  defaultOpen?: boolean;
};
export const ResponsiveModal: React.FC<ResponsiveModalProps> = t0 => {
  const $ = _c(23);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const isDesktop = useIsAboveMobile();
  let commonProps;
  let onAnimationEnd;
  let repositionInputs;
  let shouldScaleBackground;
  let t1;
  if ($[3] !== props) {
    ({
      dismissible: t1,
      repositionInputs,
      shouldScaleBackground,
      onAnimationEnd,
      ...commonProps
    } = props);
    $[3] = props;
    $[4] = commonProps;
    $[5] = onAnimationEnd;
    $[6] = repositionInputs;
    $[7] = shouldScaleBackground;
    $[8] = t1;
  } else {
    commonProps = $[4];
    onAnimationEnd = $[5];
    repositionInputs = $[6];
    shouldScaleBackground = $[7];
    t1 = $[8];
  }
  const dismissible = t1 === undefined ? true : t1;
  let t2;
  if ($[9] !== dismissible || $[10] !== isDesktop) {
    t2 = {
      isDesktop,
      dismissible
    };
    $[9] = dismissible;
    $[10] = isDesktop;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== children || $[13] !== commonProps || $[14] !== dismissible || $[15] !== isDesktop || $[16] !== onAnimationEnd || $[17] !== repositionInputs || $[18] !== shouldScaleBackground) {
    t3 = isDesktop ? <Dialog {...commonProps}>{children}</Dialog> : <Drawer {...commonProps} dismissible={dismissible} repositionInputs={repositionInputs} shouldScaleBackground={shouldScaleBackground} onAnimationEnd={onAnimationEnd}>{children}</Drawer>;
    $[12] = children;
    $[13] = commonProps;
    $[14] = dismissible;
    $[15] = isDesktop;
    $[16] = onAnimationEnd;
    $[17] = repositionInputs;
    $[18] = shouldScaleBackground;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t2 || $[21] !== t3) {
    t4 = <ResponsiveModalContext.Provider value={t2}>{t3}</ResponsiveModalContext.Provider>;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
};
export const ResponsiveModalTrigger: React.FC<React.ComponentProps<typeof DrawerTrigger> & React.ComponentProps<typeof DialogTrigger>> = props => {
  const $ = _c(3);
  const {
    isDesktop
  } = useResponsiveModal();
  let t0;
  if ($[0] !== isDesktop || $[1] !== props) {
    t0 = isDesktop ? <DialogTrigger {...props} /> : <DrawerTrigger {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const ResponsiveModalClose: React.FC<React.ComponentProps<typeof DrawerClose> & React.ComponentProps<typeof DialogClose>> = props => {
  const $ = _c(3);
  const {
    isDesktop
  } = useResponsiveModal();
  let t0;
  if ($[0] !== isDesktop || $[1] !== props) {
    t0 = isDesktop ? <DialogClose {...props} /> : <DrawerClose {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const ResponsiveModalContent = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof DrawerContent> & React.ComponentProps<typeof DialogContent>>((t0, ref) => {
  const $ = _c(22);
  let displayHandle;
  let isNested;
  let onEscapeKeyDown;
  let onInteractOutside;
  let props;
  if ($[0] !== t0) {
    ({
      displayHandle,
      isNested,
      onInteractOutside,
      onEscapeKeyDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = displayHandle;
    $[2] = isNested;
    $[3] = onEscapeKeyDown;
    $[4] = onInteractOutside;
    $[5] = props;
  } else {
    displayHandle = $[1];
    isNested = $[2];
    onEscapeKeyDown = $[3];
    onInteractOutside = $[4];
    props = $[5];
  }
  const {
    isDesktop,
    dismissible
  } = useResponsiveModal();
  if (isDesktop) {
    let t1;
    if ($[6] !== dismissible || $[7] !== onInteractOutside) {
      t1 = event => {
        if (!dismissible) {
          event.preventDefault();
        }
        onInteractOutside?.(event);
      };
      $[6] = dismissible;
      $[7] = onInteractOutside;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== dismissible || $[10] !== onEscapeKeyDown) {
      t2 = event_0 => {
        if (!dismissible) {
          event_0.preventDefault();
        }
        onEscapeKeyDown?.(event_0);
      };
      $[9] = dismissible;
      $[10] = onEscapeKeyDown;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== props || $[13] !== ref || $[14] !== t1 || $[15] !== t2) {
      t3 = <DialogContent ref={ref} onInteractOutside={t1} onEscapeKeyDown={t2} {...props} />;
      $[12] = props;
      $[13] = ref;
      $[14] = t1;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  let t1;
  if ($[17] !== displayHandle || $[18] !== isNested || $[19] !== props || $[20] !== ref) {
    t1 = <DrawerContent ref={ref} displayHandle={displayHandle} isNested={isNested} {...props} />;
    $[17] = displayHandle;
    $[18] = isNested;
    $[19] = props;
    $[20] = ref;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  return t1;
});
ResponsiveModalContent.displayName = 'ResponsiveModalContent';
export const ResponsiveModalHeader: React.FC<React.ComponentProps<typeof DrawerHeader> & React.ComponentProps<typeof DialogHeader>> = props => {
  const $ = _c(3);
  const {
    isDesktop
  } = useResponsiveModal();
  let t0;
  if ($[0] !== isDesktop || $[1] !== props) {
    t0 = isDesktop ? <DialogHeader {...props} /> : <DrawerHeader {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const ResponsiveModalFooter: React.FC<React.ComponentProps<typeof DrawerFooter> & React.ComponentProps<typeof DialogFooter>> = props => {
  const $ = _c(3);
  const {
    isDesktop
  } = useResponsiveModal();
  let t0;
  if ($[0] !== isDesktop || $[1] !== props) {
    t0 = isDesktop ? <DialogFooter {...props} /> : <DrawerFooter {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const ResponsiveModalTitle = React.forwardRef<HTMLHeadingElement, React.ComponentProps<typeof DrawerTitle> & React.ComponentProps<typeof DialogTitle>>((props, ref) => {
  const $ = _c(4);
  const {
    isDesktop
  } = useResponsiveModal();
  let t0;
  if ($[0] !== isDesktop || $[1] !== props || $[2] !== ref) {
    t0 = isDesktop ? <DialogTitle ref={ref} {...props} /> : <DrawerTitle ref={ref} {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = ref;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
});
ResponsiveModalTitle.displayName = 'ResponsiveModalTitle';
export const ResponsiveModalDescription = React.forwardRef<HTMLParagraphElement, React.ComponentProps<typeof DrawerDescription> & React.ComponentProps<typeof DialogDescription>>((props, ref) => {
  const $ = _c(4);
  const {
    isDesktop
  } = useResponsiveModal();
  let t0;
  if ($[0] !== isDesktop || $[1] !== props || $[2] !== ref) {
    t0 = isDesktop ? <DialogDescription ref={ref} {...props} /> : <DrawerDescription ref={ref} {...props} />;
    $[0] = isDesktop;
    $[1] = props;
    $[2] = ref;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
});
ResponsiveModalDescription.displayName = 'ResponsiveModalDescription';
export const ResponsiveModalNestedRoot: React.FC<ResponsiveModalProps> = t0 => {
  const $ = _c(23);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const isDesktop = useIsAboveMobile();
  let commonProps;
  let onAnimationEnd;
  let repositionInputs;
  let shouldScaleBackground;
  let t1;
  if ($[3] !== props) {
    ({
      dismissible: t1,
      repositionInputs,
      shouldScaleBackground,
      onAnimationEnd,
      ...commonProps
    } = props);
    $[3] = props;
    $[4] = commonProps;
    $[5] = onAnimationEnd;
    $[6] = repositionInputs;
    $[7] = shouldScaleBackground;
    $[8] = t1;
  } else {
    commonProps = $[4];
    onAnimationEnd = $[5];
    repositionInputs = $[6];
    shouldScaleBackground = $[7];
    t1 = $[8];
  }
  const dismissible = t1 === undefined ? true : t1;
  let t2;
  if ($[9] !== dismissible || $[10] !== isDesktop) {
    t2 = {
      isDesktop,
      dismissible
    };
    $[9] = dismissible;
    $[10] = isDesktop;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== children || $[13] !== commonProps || $[14] !== dismissible || $[15] !== isDesktop || $[16] !== onAnimationEnd || $[17] !== repositionInputs || $[18] !== shouldScaleBackground) {
    t3 = isDesktop ? <Dialog {...commonProps}>{children}</Dialog> : <DrawerNestedRoot {...commonProps} dismissible={dismissible} repositionInputs={repositionInputs} shouldScaleBackground={shouldScaleBackground} onAnimationEnd={onAnimationEnd}>{children}</DrawerNestedRoot>;
    $[12] = children;
    $[13] = commonProps;
    $[14] = dismissible;
    $[15] = isDesktop;
    $[16] = onAnimationEnd;
    $[17] = repositionInputs;
    $[18] = shouldScaleBackground;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t2 || $[21] !== t3) {
    t4 = <ResponsiveModalContext.Provider value={t2}>{t3}</ResponsiveModalContext.Provider>;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
};